// Per-page open rights (request 118, Angela 27 Oct 2026) — the FRONTEND mirror
// of backend/app/core/page_registry.py. Same ids; a backend test fails if they
// drift. Each page has `page.<id>.view` (menu item and address). Its default is
// the gate the menu item had before — computed by the server. What a page lets
// a person DO stays behind its own permissions.

export interface PageEntry {
  module: string;
  /** Page address (locale stripped, no query). */
  route: string;
}

export const PAGE_REGISTRY: Record<string, PageEntry> = {
  appointments: { module: 'Operations', route: '/schedule' },
  questionnaires: { module: 'Operations', route: '/schedule' },
  'daily-report': { module: 'Operations', route: '/daily-report' },
  'rest-time': { module: 'Operations', route: '/operations/rest-time' },
  'cancellation-reasons': { module: 'Operations', route: '/operations/cancellation-reasons' },
  'staff-schedule': { module: 'Operations', route: '/schedule' },
  'shift-types': { module: 'Operations', route: '/settings/shift-types' },
  'roster-change-log': { module: 'Operations', route: '/roster/change-log' },
  'customer-list': { module: 'Customers', route: '/customers' },
  'create-customer': { module: 'Customers', route: '/customers/create' },
  'merge-customer': { module: 'Customers', route: '/customers/merge' },
  'customer-groups': { module: 'Customers', route: '/customers/groups' },
  'cust-fields': { module: 'Customers', route: '/customers/configurations/fields' },
  'form-templates': { module: 'Customers', route: '/customers/forms/templates' },
  'cust-fill-a-form': { module: 'Customers', route: '/customers/forms/fill' },
  'form-responses': { module: 'Customers', route: '/customers/forms/responses' },
  'leads-follow-up': { module: 'Leads', route: '/customers/leads/follow-up' },
  'leads-new': { module: 'Leads', route: '/customers/leads' },
  'leads-existing': { module: 'Leads', route: '/customers/leads' },
  'leads-finishing': { module: 'Leads', route: '/customers/leads' },
  'leads-inactive-package': { module: 'Leads', route: '/customers/leads' },
  'leads-inactive-no-package': { module: 'Leads', route: '/customers/leads' },
  'leads-dnc': { module: 'Leads', route: '/customers/leads' },
  'leads-vip': { module: 'Leads', route: '/customers/leads' },
  'leads-ycp': { module: 'Leads', route: '/customers/leads' },
  'leads-conditions': { module: 'Leads', route: '/customers/leads/settings' },
  'leads-templates': { module: 'Leads', route: '/customers/leads/templates' },
  'mkt-promotion': { module: 'Marketing', route: '/marketing/promotion' },
  'mkt-outlet-authority': { module: 'Marketing', route: '/marketing/outlet-authority' },
  'mkt-media': { module: 'Marketing', route: '/marketing/media' },
  'mkt-form-templates': { module: 'Marketing', route: '/marketing/forms/templates' },
  'mkt-fill-a-form': { module: 'Marketing', route: '/marketing/forms/fill' },
  'mkt-form-responses': { module: 'Marketing', route: '/marketing/forms/responses' },
  'create-invoice': { module: 'Sales', route: '/finance/pos/create-invoice' },
  'balance-payment': { module: 'Sales', route: '/finance/pos/balance-payment' },
  'inv-refund': { module: 'Sales', route: '/finance/pos/refund' },
  'inv-upgrade': { module: 'Sales', route: '/finance/pos/upgrade-invoice' },
  'inv-contra': { module: 'Sales', route: '/finance/pos/contra-invoice' },
  'search-invoices': { module: 'Sales', route: '/finance/search-invoices' },
  'search-usages': { module: 'Sales', route: '/finance/search-usages' },
  'create-usage': { module: 'Sales', route: '/finance/pos/create-usage' },
  'void-usage': { module: 'Sales', route: '/finance/pos/void-usage' },
  'credit-note': { module: 'Sales', route: '/finance/pos/credit-note' },
  'search-credit-notes': { module: 'Sales', route: '/finance/search-credit-notes' },
  'credit-rates': { module: 'Sales', route: '/settings/credits' },
  'credit-types': { module: 'Sales', route: '/finance/config/credit-types' },
  'sales-form-templates': { module: 'Sales', route: '/finance/forms/templates' },
  'sales-fill-a-form': { module: 'Sales', route: '/finance/forms/fill' },
  'sales-form-responses': { module: 'Sales', route: '/finance/forms/responses' },
  'approval-requests': { module: 'Sales', route: '/finance/approvals/requests' },
  'approvals': { module: 'Sales', route: '/finance/approvals' },
  'approval-settings': { module: 'Sales', route: '/finance/config/approval-settings' },
  'ihip': { module: 'Sales', route: '/finance/ihip' },
  'payment-kinds': { module: 'Sales', route: '/finance/config/payment-kinds' },
  'payment-types': { module: 'Sales', route: '/finance/config/payment-types' },
  'taxes': { module: 'Sales', route: '/finance/config/taxes' },
  'daily-close': { module: 'Sales', route: '/finance/daily-close' },
  'xero': { module: 'Sales', route: '/finance/xero' },
  'treatments': { module: 'Inventory', route: '/treatments' },
  'program-step-mapping': { module: 'Inventory', route: '/settings/program-step-mapping' },
  'products': { module: 'Inventory', route: '/inventory/products' },
  'inventory-level': { module: 'Inventory', route: '/inventory/inventory-level' },
  'stock': { module: 'Inventory', route: '/inventory/stock' },
  'stocktake': { module: 'Inventory', route: '/inventory/stocktake' },
  'staff-directory': { module: 'HR', route: '/staff' },
  'audit-trail': { module: 'HR', route: '/hr/audit-trail' },
  'salary': { module: 'HR', route: '/salary' },
  'commission': { module: 'HR', route: '/commission' },
  'claim-submission': { module: 'HR', route: '/hr/claims' },
  'claim-responses': { module: 'HR', route: '/hr/claims/responses' },
  'promotion-status': { module: 'HR', route: '/staff-promotion/status' },
  'promotion-history': { module: 'HR', route: '/staff-promotion/history' },
  'promotion-template': { module: 'HR', route: '/staff-promotion/template' },
  'review-responses': { module: 'HR', route: '/performance-review/responses' },
  'company-outlets': { module: 'HR', route: '/company/outlets' },
  'meeting-log': { module: 'HR', route: '/meetings/log' },
  'training-log': { module: 'HR', route: '/training/log' },
  'training-schedule': { module: 'HR', route: '/training/schedule' },
  'training-matrix': { module: 'HR', route: '/training/matrix' },
  'training-sop-library': { module: 'HR', route: '/training/sop-library' },
  'hr-form-templates': { module: 'HR', route: '/hr/forms/templates' },
  'hr-fill-a-form': { module: 'HR', route: '/hr/forms/fill' },
  'hr-form-responses': { module: 'HR', route: '/hr/forms/responses' },
  'hr-ai-history': { module: 'HR', route: '/hr/yakson-ai/history' },
  'hr-ai-qa': { module: 'HR', route: '/hr/yakson-ai/qa' },
  'hr-ai-knowledge': { module: 'HR', route: '/hr/yakson-ai/knowledge' },
  'settings-salary': { module: 'Settings', route: '/settings/salary' },
  'settings-tablets': { module: 'Settings', route: '/settings/tablets' },
};

export const pageViewKey = (id: string): `page.${string}.view` => `page.${id}.view`;

/**
 * Fix list G7: several menu pages share one address (/schedule: Appointments,
 * Questionnaires, Staff Roster; /customers/leads: the Leads lists), so the
 * route check passes when ANY of them is open. Each page clamps its own tabs:
 * a tab whose page is closed for this person falls to the first open tab.
 * `tabPages` maps a tab to its page id (null = no page right of its own).
 */
export function clampToOpenTab<T extends string>(
  requested: T,
  tabPages: readonly (readonly [T, string | null])[],
  canOpen: (pageId: string) => boolean,
): T {
  const isOpen = (tab: T) => {
    const entry = tabPages.find(([key]) => key === tab);
    return !entry || entry[1] === null || canOpen(entry[1]);
  };
  if (isOpen(requested)) return requested;
  return tabPages.find(([key]) => isOpen(key))?.[0] ?? requested;
}
